
import { createRoot, hydrateRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App.tsx'
import './index.css'

// Function to handle rendering with error capturing
const renderApp = () => {
  const rootElement = document.getElementById("root");

  // Add sitemap link in the document head
  const addSitemapLink = () => {
    const existingLink = document.querySelector('link[rel="sitemap"]');
    if (!existingLink) {
      const link = document.createElement('link');
      link.rel = 'sitemap';
      link.type = 'application/xml';
      link.href = '/sitemap.xml';
      document.head.appendChild(link);
    }
  };
  
  // Enhanced widget monitoring and initialization
  const setupLovableWidgetMonitoring = () => {
    console.log('🔧 Setting up Lovable widget monitoring...');
    
    // Check for widget availability periodically
    let checkCount = 0;
    const maxChecks = 20; // Check for up to 10 seconds (20 * 500ms)
    
    const checkForWidget = () => {
      checkCount++;
      console.log(`📡 Widget check ${checkCount}/${maxChecks}:`, {
        scriptLoaded: !!document.querySelector('script[src*="gptengineer.js"]'),
        lovableExists: !!window.lovable,
        widgetMethods: window.lovable ? Object.keys(window.lovable) : 'N/A'
      });
      
      if (window.lovable || checkCount >= maxChecks) {
        if (window.lovable) {
          console.log('✅ Lovable widget detected and ready');
        } else {
          console.log('⚠️ Lovable widget not detected after maximum checks');
        }
        return;
      }
      
      setTimeout(checkForWidget, 500);
    };
    
    // Start checking after initial page load
    setTimeout(checkForWidget, 100);
    
    // Monitor for script loading
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            if (element.tagName === 'SCRIPT' && element.getAttribute('src')?.includes('gptengineer.js')) {
              console.log('📜 Lovable script detected in DOM');
            }
          }
        });
      });
    });
    
    observer.observe(document.head, { childList: true, subtree: true });
    
    // Clean up observer after 30 seconds
    setTimeout(() => {
      observer.disconnect();
      console.log('🧹 Lovable widget monitoring observer cleaned up');
    }, 30000);
  };

  if (!rootElement) {
    console.error("Failed to find the root element");
    // Create a fallback root element if the original one doesn't exist
    const fallbackRoot = document.createElement("div");
    fallbackRoot.id = "root";
    document.body.appendChild(fallbackRoot);
    
    try {
      setupLovableWidgetMonitoring();
      const app = (
        <BrowserRouter>
          <App />
        </BrowserRouter>
      );
      
      if (import.meta.env.PROD) {
        hydrateRoot(fallbackRoot, app);
      } else {
        createRoot(fallbackRoot).render(app);
      }
      
      addSitemapLink();
      console.log("Application mounted on fallback root element");
    } catch (error) {
      console.error("Critical rendering error:", error);
      document.body.innerHTML = '<div style="padding: 20px; text-align: center;"><h2>Critical Error</h2><p>The application could not start. Please try again later.</p></div>';
    }
  } else {
    try {
      setupLovableWidgetMonitoring();
      const app = (
        <BrowserRouter>
          <App />
        </BrowserRouter>
      );
      
      if (import.meta.env.PROD) {
        hydrateRoot(rootElement, app);
      } else {
        createRoot(rootElement).render(app);
      }
      
      addSitemapLink();
    } catch (error) {
      console.error("Failed to render the application:", error);
      // Display a user-friendly error message
      if (rootElement) {
        rootElement.innerHTML = '<div style="padding: 20px; text-align: center;"><h2>Something went wrong</h2><p>Please try refreshing the page or contact support if the issue persists.</p></div>';
      }
    }
  }
};

// Use requestIdleCallback for non-critical initialization
if ('requestIdleCallback' in window) {
  // @ts-ignore - TypeScript might not have the correct type definition
  window.requestIdleCallback(() => {
    renderApp();
  }, { timeout: 2000 });
} else {
  // Fallback for browsers that don't support requestIdleCallback
  setTimeout(renderApp, 10);
}
